import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';
import Container from '../../layouts/Container';
import { useUsers } from '../../context/UserContext';
import {
  createResultsFormat,
  deleteResultsFormat,
  getResultsFormatById,
  getResultsFormats,
  updateResultsFormat,
} from '../../services/project/resultsFormatService';

const SELECT_VALUE_TYPES = new Set(['dropdown', 'radio', 'checkbox']);

const normalizeOptions = (options) => {
  if (Array.isArray(options)) return options;
  if (typeof options === 'string') {
    return options
      .split(',')
      .map((opt) => opt.trim())
      .filter(Boolean);
  }
  return [];
};

const getPayload = (response) => response?.data || response;

const mapRuleForForm = (rule = {}, fallbackIndex = 0) => ({
  label: rule.label || '',
  fieldId: rule.fieldId ? String(rule.fieldId) : '',
  fieldValue: rule.fieldValue ?? '',
  key: `${Date.now()}-${fallbackIndex}`,
});

const mapFormatForForm = (format = {}) => ({
  name: format.name || '',
  description: format.description || '',
  isActive: format.isActive !== false,
  rules: (format.rules || []).map((rule, idx) => mapRuleForForm(rule, idx)),
});

const getFieldMeta = (fieldMap, fieldId) => {
  if (!fieldId) return null;
  return fieldMap.get(Number(fieldId)) || null;
};

const ResultFormatsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useUsers();

  const [formats, setFormats] = useState([]);
  const [formGroups, setFormGroups] = useState([]);
  const [selectedFormGroupId, setSelectedFormGroupId] = useState('');
  const [fieldCatalog, setFieldCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formState, setFormState] = useState({
    name: '',
    description: '',
    isActive: true,
    rules: [mapRuleForForm({}, 0)],
  });

  const filteredFieldCatalog = useMemo(
    () => fieldCatalog.filter((field) => String(field.formGroupId) === String(selectedFormGroupId)),
    [fieldCatalog, selectedFormGroupId]
  );

  const fieldMap = useMemo(() => {
    const map = new Map();
    filteredFieldCatalog.forEach((field) => map.set(Number(field.id), field));
    return map;
  }, [filteredFieldCatalog]);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      navigate(`/projects/${projectId}/result`);
    }
  }, [user, isSuperAdmin, navigate, projectId]);

  const fetchFieldCatalog = async () => {
    const groupsRes = await api.get(`/projects/${projectId}/formgroups`);
    const groups = groupsRes.data || [];

    const formsResponses = await Promise.all(
      groups.map((group) => api.get(`/projects/${projectId}/formgroups/${group.id}`))
    );

    const fields = [];

    formsResponses.forEach((res, index) => {
      const forms = res.data || [];
      const formGroup = groups[index];

      forms.forEach((form) => {
        (form.fields || []).forEach((field) => {
          if (!field?.id || field.type === 'label' || field.type === 'address') return;

          fields.push({
            id: field.id,
            label: field.label || field.name || `Field ${field.id}`,
            name: field.name || `field_${field.id}`,
            type: field.type,
            options: normalizeOptions(field.options),
            formName: form.name || '',
            formGroupId: formGroup?.id,
            formGroupName: formGroup?.name || '',
          });
        });
      });
    });

    return fields.sort((a, b) => a.label.localeCompare(b.label));
  };

  const fetchFormats = async () => {
    const res = await getResultsFormats(projectId);
    const payload = getPayload(res);
    return payload?.resultsFormats || payload?.data || payload || [];
  };

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [fetchedFormats, fetchedFields, formGroupsRes] = await Promise.all([
        fetchFormats(),
        fetchFieldCatalog(),
        api.get(`/projects/${projectId}/formgroups`),
      ]);

      const groups = formGroupsRes.data || [];
      const activeGroup = groups.find((group) => group.isActive);
      const defaultGroupId = String(activeGroup?.id || groups[0]?.id || '');

      setFormats(fetchedFormats);
      setFieldCatalog(fetchedFields);
      setFormGroups(groups);
      setSelectedFormGroupId(defaultGroupId);
    } catch (error) {
      console.error('Error loading result formats page:', error);
      toast.error(error.response?.data?.errorMessage || 'Failed to load result formats.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, [projectId]);

  useEffect(() => {
    if (!selectedFormGroupId) return;

    setFormState((prev) => ({
      ...prev,
      rules: prev.rules.map((rule) => {
        if (!rule.fieldId) return rule;
        return fieldMap.has(Number(rule.fieldId))
          ? rule
          : { ...rule, fieldId: '', fieldValue: '' };
      }),
    }));
  }, [fieldMap, selectedFormGroupId]);

  const resetForm = () => {
    setEditingId(null);
    setFormState({
      name: '',
      description: '',
      isActive: true,
      rules: [mapRuleForForm({}, 0)],
    });
  };

  const setRule = (index, patch) => {
    setFormState((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, idx) => (idx === index ? { ...rule, ...patch } : rule)),
    }));
  };

  const addRule = () => {
    setFormState((prev) => ({
      ...prev,
      rules: [...prev.rules, mapRuleForForm({}, prev.rules.length)],
    }));
  };

  const removeRule = (index) => {
    setFormState((prev) => {
      const nextRules = prev.rules.filter((_, idx) => idx !== index);
      return {
        ...prev,
        rules: nextRules.length > 0 ? nextRules : [mapRuleForForm({}, 0)],
      };
    });
  };

  const validateForm = () => {
    if (!selectedFormGroupId) {
      return 'Form group is required.';
    }

    if (!formState.name.trim()) {
      return 'Name is required.';
    }

    if (!Array.isArray(formState.rules) || formState.rules.length === 0) {
      return 'At least one rule is required.';
    }

    for (let i = 0; i < formState.rules.length; i += 1) {
      const rule = formState.rules[i];
      const indexLabel = `Rule ${i + 1}`;

      if (!rule.label?.trim()) {
        return `${indexLabel}: label is required.`;
      }

      if (!rule.fieldId) {
        return `${indexLabel}: field is required.`;
      }

      const fieldMeta = getFieldMeta(fieldMap, rule.fieldId);

      if (!fieldMeta) {
        return `${indexLabel}: selected field is invalid for the selected form group.`;
      }

      if (SELECT_VALUE_TYPES.has(fieldMeta.type)) {
        if (rule.fieldValue === '' || rule.fieldValue === null || rule.fieldValue === undefined) {
          return `${indexLabel}: value is required for ${fieldMeta.type} fields.`;
        }

        if (fieldMeta.options.length > 0 && !fieldMeta.options.includes(rule.fieldValue)) {
          return `${indexLabel}: selected value does not exist in field options.`;
        }
      }
    }

    return null;
  };

  const buildPayload = () => ({
    name: formState.name.trim(),
    description: formState.description.trim(),
    isActive: !!formState.isActive,
    rules: formState.rules.map((rule) => {
      const fieldMeta = getFieldMeta(fieldMap, rule.fieldId);
      const hasSelectableValue = fieldMeta && SELECT_VALUE_TYPES.has(fieldMeta.type);

      return {
        label: rule.label.trim(),
        fieldId: Number(rule.fieldId),
        fieldValue: hasSelectableValue ? rule.fieldValue : null,
      };
    }),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();

      if (editingId) {
        await updateResultsFormat(projectId, editingId, payload);
        toast.success('Result format updated.');
      } else {
        await createResultsFormat(projectId, payload);
        toast.success('Result format created.');
      }

      const refreshed = await fetchFormats();
      setFormats(refreshed);
      resetForm();
    } catch (error) {
      console.error('Error saving result format:', error);
      toast.error(error.response?.data?.errorMessage || 'Failed to save result format.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (formatId) => {
    try {
      const res = await getResultsFormatById(projectId, formatId);
      const payload = getPayload(res);
      const format = payload?.resultsFormat || payload?.data || payload;

      setEditingId(format.id);
      setFormState(mapFormatForForm(format));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading format details:', error);
      toast.error(error.response?.data?.errorMessage || 'Failed to load format details.');
    }
  };

  const handleDelete = async (formatId) => {
    const confirmed = window.confirm('Delete this result format?');
    if (!confirmed) return;

    try {
      await deleteResultsFormat(projectId, formatId);
      toast.success('Result format deleted.');

      setFormats((prev) => prev.filter((format) => format.id !== formatId));

      if (editingId === formatId) {
        resetForm();
      }
    } catch (error) {
      console.error('Error deleting result format:', error);
      toast.error(error.response?.data?.errorMessage || 'Failed to delete result format.');
    }
  };

  if (user && !isSuperAdmin) {
    return null;
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Result Format Manager</h2>
        <Button as={Link} to={`/projects/${projectId}/result`} variant="outline-secondary">
          Back to Result
        </Button>
      </div>

      <Alert variant="info" className="mb-4">
        Define reusable result formats and rules. Fields are scoped by selected form group.
      </Alert>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{editingId ? 'Edit Result Format' : 'New Result Format'}</h5>
                {editingId && (
                  <Button variant="outline-secondary" size="sm" onClick={resetForm}>
                    Cancel Edit
                  </Button>
                )}
              </div>

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Form Group</Form.Label>
                      <Form.Select
                        value={selectedFormGroupId}
                        onChange={(e) => setSelectedFormGroupId(e.target.value)}
                      >
                        <option value="">Select form group</option>
                        {formGroups.map((group) => (
                          <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        value={formState.name}
                        onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        value={formState.description}
                        onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={1}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Check
                        type="switch"
                        id="results-format-active"
                        label={formState.isActive ? 'Active' : 'Inactive'}
                        checked={formState.isActive}
                        onChange={(e) => setFormState((prev) => ({ ...prev, isActive: e.target.checked }))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-2">Rules</h6>

                {formState.rules.map((rule, index) => {
                  const fieldMeta = getFieldMeta(fieldMap, rule.fieldId);
                  const requiresValue = fieldMeta && SELECT_VALUE_TYPES.has(fieldMeta.type);
                  const options = fieldMeta?.options || [];

                  return (
                    <Card key={rule.key} className="mb-3 border-light">
                      <Card.Body>
                        <Row>
                          <Col md={3}>
                            <Form.Group className="mb-2">
                              <Form.Label>Rule Label</Form.Label>
                              <Form.Control
                                value={rule.label}
                                onChange={(e) => setRule(index, { label: e.target.value })}
                                placeholder={`Rule ${index + 1}`}
                                required
                              />
                            </Form.Group>
                          </Col>

                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label>Field</Form.Label>
                              <Form.Select
                                value={rule.fieldId}
                                onChange={(e) => {
                                  const nextFieldId = e.target.value;
                                  const nextField = getFieldMeta(fieldMap, nextFieldId);
                                  const nextRequiresValue = nextField && SELECT_VALUE_TYPES.has(nextField.type);
                                  setRule(index, {
                                    fieldId: nextFieldId,
                                    fieldValue: nextRequiresValue ? '' : null,
                                  });
                                }}
                                required
                              >
                                <option value="">Select field</option>
                                {filteredFieldCatalog.map((field) => (
                                  <option key={field.id} value={field.id}>
                                    {field.label} ({field.type}) - {field.formGroupName} / {field.formName}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          <Col md={4}>
                            <Form.Group className="mb-2">
                              <Form.Label>Field Value</Form.Label>
                              {requiresValue ? (
                                <Form.Select
                                  value={rule.fieldValue}
                                  onChange={(e) => setRule(index, { fieldValue: e.target.value })}
                                  required
                                >
                                  <option value="">Select value</option>
                                  {options.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </Form.Select>
                              ) : (
                                <Form.Control value="Not required for this field type" disabled />
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={1} className="d-flex align-items-end">
                            <Button
                              variant="outline-danger"
                              className="w-100"
                              onClick={() => removeRule(index)}
                            >
                              x
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  );
                })}

                <div className="d-flex justify-content-between">
                  <Button type="button" variant="outline-primary" onClick={addRule}>
                    Add Rule
                  </Button>

                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update Format' : 'Create Format'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <h5 className="mb-3">Saved Formats</h5>
              {formats.length === 0 ? (
                <Alert variant="secondary" className="mb-0">No result formats yet.</Alert>
              ) : (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Rules</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formats.map((format) => (
                      <tr key={format.id}>
                        <td>{format.name}</td>
                        <td>{format.description || '-'}</td>
                        <td>{Array.isArray(format.rules) ? format.rules.length : 0}</td>
                        <td>
                          <Badge bg={format.isActive ? 'success' : 'secondary'}>
                            {format.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Button size="sm" variant="outline-primary" onClick={() => handleEdit(format.id)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(format.id)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default ResultFormatsPage;
